import React, { useState } from 'react';
import type { EntityType } from '../types';
import type { Attachment } from '@mariana/labos-client';
import { formatDate } from '../utils/formatDate';
import './EntityDetail.css';

interface AttachmentsTabProps {
  entityType: EntityType;
  attachments: Attachment[];
  onNavigate: (path: string) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileTypeIcon(contentType: string): React.ReactNode {
  if (contentType.startsWith('lab-report') || contentType.includes('pdf')) {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    );
  }
  if (contentType.startsWith('photo') || contentType.includes('image')) {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
      </svg>
    );
  }
  if (contentType.startsWith('icp') || contentType.includes('data') || contentType.includes('json')) {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
      <polyline points="13 2 13 9 20 9"/>
    </svg>
  );
}

export const EntityDetailAttachmentsTab: React.FC<AttachmentsTabProps> = ({
  entityType,
  attachments,
  onNavigate,
}) => {
  const [expandedAttachment, setExpandedAttachment] = useState<number | null>(null);

  if (attachments.length === 0) {
    return <div className="events-empty-state">No attachments found for this {entityType}.</div>;
  }

  return (
    <table className="entity-events-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>Type</th>
          <th>Filename</th>
          <th>Content Type</th>
          <th>Size</th>
          <th>Hash</th>
          <th>Uploaded By</th>
          <th>Created</th>
        </tr>
      </thead>
      <tbody>
        {attachments.map((att) => {
          const contentType = att.attributes?.content_type || '';
          const hasData = att.attributes?.data !== undefined && att.attributes?.data !== null;
          const isExpanded = expandedAttachment === att.id;
          return (
            <React.Fragment key={att.id}>
              <tr>
                <td>
                  <span
                    className="events-entity-link"
                    onClick={() => onNavigate(`/events?entity_type=attachment&entity_id=${att.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') onNavigate(`/events?entity_type=attachment&entity_id=${att.id}`);
                    }}
                  >
                    {att.id}
                  </span>
                </td>
                <td title={contentType || 'unknown'} style={{ color: 'var(--text-muted)', verticalAlign: 'middle' }}>
                  {getFileTypeIcon(contentType)}
                </td>
                <td>
                  {hasData ? (
                    <span
                      style={{ cursor: 'pointer', color: 'var(--accent-blue)' }}
                      onClick={() => setExpandedAttachment(isExpanded ? null : (att.id ?? null))}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setExpandedAttachment(isExpanded ? null : (att.id ?? null));
                      }}
                    >
                      {att.attributes?.filename || '-'}
                      <span style={{ marginLeft: '0.25rem', display: 'inline-block', verticalAlign: 'middle' }}>
                        {isExpanded ? (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="18 15 12 9 6 15"/>
                          </svg>
                        ) : (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 12 15 18 9"/>
                          </svg>
                        )}
                      </span>
                    </span>
                  ) : (
                    att.attributes?.filename || '-'
                  )}
                </td>
                <td>
                  <span className="changed-attr-tag">{contentType || '-'}</span>
                </td>
                <td>{att.attributes?.file_size ? formatFileSize(att.attributes.file_size) : '-'}</td>
                <td>
                  {att.attributes?.content_hash ? (
                    <code style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.75rem', color: 'var(--text-muted)' }}
                      title={att.attributes.content_hash}
                    >
                      {String(att.attributes.content_hash).slice(0, 8)}
                    </code>
                  ) : '-'}
                </td>
                <td>{att.attributes?.uploaded_by || '-'}</td>
                <td>{att.created_at ? formatDate(att.created_at) : '-'}</td>
              </tr>
              {hasData && isExpanded && (
                <tr>
                  <td colSpan={8} style={{ padding: '0.5rem', background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto', maxHeight: '200px', padding: '0.5rem' }}>
                      {typeof att.attributes?.data === 'object'
                        ? JSON.stringify(att.attributes.data, null, 2)
                        : String(att.attributes?.data)}
                    </pre>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};
